/**
 * Slot <-> screen/position mapping.
 *
 * A shop has N screens, each showing a configured number of tickets. Slot
 * numbers run continuously across screens (screen 1 is slots 1..C1, screen 2
 * is C1+1..C1+C2, and so on). `perScreenCounts` is 0-indexed by screen, i.e.
 * perScreenCounts[0] is screen 1's ticket count.
 *
 * The dashboard already computes the forward direction inline
 * (screenOneSettings.ticketsCount + index + 1); `slotNumberFor` is the reusable
 * version of that. `resolveSlot` is the inverse the Activate popup needs.
 */

export interface ResolvedSlot {
  /** 1-based screen number */
  screenNumber: number;
  /** 0-based position within that screen (matches shopandticketlink.ticketPosition) */
  ticketPosition: number;
}

/**
 * Forward: the continuous slot number for a 0-based position on a screen.
 * @param screenNumber 1-based screen number
 * @param index 0-based position within the screen
 * @param perScreenCounts ticket count per screen (0-indexed by screen)
 */
export const slotNumberFor = (
  screenNumber: number,
  index: number,
  perScreenCounts: number[],
): number => {
  const offset = perScreenCounts
    .slice(0, Math.max(0, screenNumber - 1))
    .reduce((sum, count) => sum + count, 0);
  return offset + index + 1;
};

/**
 * Inverse: the screen + 0-based position for a continuous 1-based slot number.
 * Returns null when the slot is out of range (<= 0 or beyond the total).
 * e.g. resolveSlot(30, [20, 20]) => { screenNumber: 2, ticketPosition: 9 }
 */
export const resolveSlot = (
  globalSlot: number,
  perScreenCounts: number[],
): ResolvedSlot | null => {
  if (!Number.isInteger(globalSlot) || globalSlot <= 0) {
    return null;
  }

  let remaining = globalSlot;
  for (let screen = 0; screen < perScreenCounts.length; screen += 1) {
    const count = perScreenCounts[screen];
    if (remaining <= count) {
      return { screenNumber: screen + 1, ticketPosition: remaining - 1 };
    }
    remaining -= count;
  }

  return null;
};
